import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import { SingleValue } from 'react-select';

import { IStore } from '../../../../services/api/troubleshooting-service';
import CurrentPortalStore from '../../../../stores/current-portal-store';
import { IOption, SelectDropdown } from '../../../../components/inputs/select-dropdown/select-dropdown';
import { Button } from '../../../../components/inputs/button/button';
import { Textarea } from '../../../../components/inputs/textarea/textarea';
import { ConfirmModal } from '../../../../components/modal/components/confirm-modal/confirm-modal';
import TroubleshootingStore from '../../troubleshooting-page-store';

import StoreFeaturesDisplay from './store-features-display/store-features-display';
import './store-features-page.scss';

const EPHEMERAL_WEBHOOK_ENDPOINTS_FEATURE_ID = 100148;
const EXCEPTION_REASON_MIN_LENGTH = 3;
const EXCEPTION_REASON_MAX_LENGTH = 500;

type PendingExceptionChange = {
    action: 'activate' | 'deactivate';
    featureId: number;
};

const StoreFeaturesPage: React.FC = observer(() => {
    const [chosenStore, setChosenStore] = useState<IOption | null>(null);
    const [shopifyResponse, setShopifyResponse] = useState<any>(null);
    const [isActivatingFeature, setIsActivatingFeature] = useState<boolean>(false);
    const [activatingFeatureId, setActivatingFeatureId] = useState<number | null>(null);
    const [isDeactivatingFeature, setIsDeactivatingFeature] = useState<boolean>(false);
    const [deactivatingFeatureId, setDeactivatingFeatureId] = useState<number | null>(null);
    const [isSettingQuota, setIsSettingQuota] = useState<boolean>(false);
    const [settingQuotaFeatureId, setSettingQuotaFeatureId] = useState<number | null>(null);
    const [pendingExceptionChange, setPendingExceptionChange] = useState<PendingExceptionChange | null>(null);
    const [exceptionReason, setExceptionReason] = useState<string>('');

    useEffect(() => {
        TroubleshootingStore.fetchStores();
        CurrentPortalStore.load();
    }, []);

    const portalId = CurrentPortalStore.currentPortal.id;
    const portalName = CurrentPortalStore.currentPortal.name;
    const portalDisplayName = CurrentPortalStore.currentPortal.displayName;

    useEffect(() => {
        if (chosenStore?.value !== portalId && portalId && portalName) {
            setChosenStore({
                value: portalId,
                label: portalName + (portalDisplayName ? ' (' + portalDisplayName + ')' : '')
            });
        }
    }, [portalId, portalName, portalDisplayName, chosenStore?.value]);

    /** patch to prevent react warning */
    const __shopifyResponse = TroubleshootingStore.shopifyResponse;
    useEffect(() => {
        setShopifyResponse(toJS(TroubleshootingStore.shopifyResponse));
    }, [__shopifyResponse]);

    const generateSelectValues = (): IOption[] | [] => {
        return TroubleshootingStore.stores?.map((el: IStore): IOption => {
            return {
                value: el.id,
                label: el.name + (el.display_name ? ' (' + el.display_name + ')' : '')
            }
        }) || [];
    }

    const onStoreChangeHandler = (val: SingleValue<IOption>) => {
        if (val) {
            setChosenStore(val);
            // Clear results when store changes
            setShopifyResponse(null);
        }
    }

    const performFetch = () => {
        if (chosenStore?.value) {
            TroubleshootingStore.fetchStoreFeatures(chosenStore.value);
        }
    }

    const activateFeature = async (featureId: number, reason?: string) => {
        if (chosenStore?.value) {
            setIsActivatingFeature(true);
            setActivatingFeatureId(featureId);

            try {
                await TroubleshootingStore.activateFeatureForShop(chosenStore.value, featureId, reason);
                // Refresh the features data after activation
                await TroubleshootingStore.fetchStoreFeatures(chosenStore.value);
            } finally {
                setIsActivatingFeature(false);
                setActivatingFeatureId(null);
            }
        }
    };

    const deactivateFeature = async (featureId: number, reason?: string) => {
        if (chosenStore?.value) {
            setIsDeactivatingFeature(true);
            setDeactivatingFeatureId(featureId);

            try {
                await TroubleshootingStore.deactivateFeatureForShop(chosenStore.value, featureId, reason);
                // Refresh the features data after deactivation
                await TroubleshootingStore.fetchStoreFeatures(chosenStore.value);
            } finally {
                setIsDeactivatingFeature(false);
                setDeactivatingFeatureId(null);
            }
        }
    };

    const requestExceptionChange = (action: PendingExceptionChange['action'], featureId: number) => {
        setExceptionReason('');
        setPendingExceptionChange({ action, featureId });
    };

    const handleActivateFeature = async (featureId: number) => {
        if (featureId === EPHEMERAL_WEBHOOK_ENDPOINTS_FEATURE_ID) {
            requestExceptionChange('activate', featureId);
            return;
        }

        await activateFeature(featureId);
    };

    const handleDeactivateFeature = async (featureId: number) => {
        if (featureId === EPHEMERAL_WEBHOOK_ENDPOINTS_FEATURE_ID) {
            requestExceptionChange('deactivate', featureId);
            return;
        }

        await deactivateFeature(featureId);
    };

    const closeExceptionChangeModal = () => {
        setPendingExceptionChange(null);
        setExceptionReason('');
    };

    const confirmExceptionChange = async () => {
        if (!pendingExceptionChange || exceptionReason.trim().length < EXCEPTION_REASON_MIN_LENGTH) {
            return;
        }

        const reason = exceptionReason.trim();
        if (pendingExceptionChange.action === 'activate') {
            await activateFeature(pendingExceptionChange.featureId, reason);
        } else {
            await deactivateFeature(pendingExceptionChange.featureId, reason);
        }

        closeExceptionChangeModal();
    };

    const handleSetQuota = async (featureId: number, quota: number) => {
        if (chosenStore?.value) {
            setIsSettingQuota(true);
            setSettingQuotaFeatureId(featureId);

            try {
                await TroubleshootingStore.setFeatureQuota(chosenStore.value, featureId, quota);
                // Refresh the features data after setting quota
                await TroubleshootingStore.fetchStoreFeatures(chosenStore.value);
            } finally {
                setIsSettingQuota(false);
                setSettingQuotaFeatureId(null);
            }
        }
    };

    const canPerformFetch = !!chosenStore?.value;

    return (
        <div className="store-features-page">
            <div className="card">
                <h4>Request Information</h4>
                <div className="controls">
                    <p className="head5">Store</p>
                    <SelectDropdown
                        required
                        value={chosenStore || undefined}
                        placeholder="Type to find and select store"
                        options={generateSelectValues()}
                        onChange={onStoreChangeHandler}
                    />
                </div>
                <Button
                    disabled={!canPerformFetch}
                    text="Fetch"
                    onClick={performFetch}
                />
            </div>

            {shopifyResponse?.data ? (
                <div className="card results-card">
                    <StoreFeaturesDisplay
                        features={shopifyResponse.data}
                        storeId={chosenStore?.value}
                        onActivateFeature={handleActivateFeature}
                        onDeactivateFeature={handleDeactivateFeature}
                        onSetQuota={handleSetQuota}
                        isActivating={isActivatingFeature}
                        activatingFeatureId={activatingFeatureId}
                        isDeactivating={isDeactivatingFeature}
                        deactivatingFeatureId={deactivatingFeatureId}
                        isSettingQuota={isSettingQuota}
                        settingQuotaFeatureId={settingQuotaFeatureId}
                    />
                </div>
            ) : null}

            {pendingExceptionChange ? (
                <ConfirmModal
                    title={pendingExceptionChange.action === 'activate'
                        ? 'Grant ephemeral webhook endpoint exception'
                        : 'Remove ephemeral webhook endpoint exception'}
                    text={(
                        <div className="exception-reason">
                            <p>
                                {pendingExceptionChange.action === 'activate'
                                    ? 'This allows the merchant to register and use known public ephemeral webhook endpoints. Enter the approved business reason.'
                                    : 'This immediately blocks future registration and delivery to known public ephemeral webhook endpoints. Enter the removal reason.'}
                            </p>
                            <Textarea
                                required
                                value={exceptionReason}
                                placeholder="Reason"
                                onChange={setExceptionReason}
                                disabled={isActivatingFeature || isDeactivatingFeature}
                                maxLength={EXCEPTION_REASON_MAX_LENGTH}
                                showCharactersCount
                            />
                        </div>
                    )}
                    isCloseDisabled={isActivatingFeature || isDeactivatingFeature}
                    cancelButton={{
                        title: 'Cancel',
                        onClick: closeExceptionChangeModal,
                        disabled: isActivatingFeature || isDeactivatingFeature,
                    }}
                    okButton={{
                        title: pendingExceptionChange.action === 'activate' ? 'Grant exception' : 'Remove exception',
                        onClick: confirmExceptionChange,
                        disabled: exceptionReason.trim().length < EXCEPTION_REASON_MIN_LENGTH,
                        hasLoader: true,
                        isLoading: isActivatingFeature || isDeactivatingFeature,
                    }}
                />
            ) : null}
        </div>
    );
});

export default StoreFeaturesPage;

